<template>
  <mds-layout-grid>
    <mds-row>
      <mds-col :cols="9">
        <mds-fieldset :class="$style['field-item']" size="small">
          <span>{{ feeScheduleTypeText }}</span>
          <span :class="$style['max-discount-description']">{{ maxDiscountDescription }}</span>
        </mds-fieldset>
        <mds-table>
          <mds-thead>
            <mds-th
              v-for="(header, index) in headers"
              :key="index"
              :right-aligned="header.align === 'right'"
              :style="header.style"
            >
              {{ header.text }}
            </mds-th>
          </mds-thead>
          <mds-tbody>
            <mds-tr v-for="(row, rowIndex) in rows" :key="row.sequence">
              <mds-td
                v-for="(header, colIndex) in headers"
                :key="colIndex"
                :right-aligned="header.align === 'right'"
              >
                <template v-if="header.fieldName === 'sequence'">
                  <span :class="$style['sequence-cell']">
                    <template v-if="rowIndex === 0">
                      {{ getFirstRowSequenceLabel() }}
                    </template>
                    <template v-else-if="rowIndex === rows.length - 1">
                      {{ getLastRowSequenceLabel() }}
                    </template>
                    <template v-else>
                      {{ getMiddleRowSequenceLabel(rowIndex) }}
                    </template>
                  </span>
                </template>
                <template v-else-if="header.fieldName === 'annualAssetBasedFee'">
                  <a
                    v-if="hasFeeBreakdown"
                    :id="`fee-breakdown-${rowIndex}`"
                    href="#"
                    :class="$style['fee-breakdown']"
                    @click.prevent
                    @mouseover="handleMouseOver(rowIndex)"
                    @mouseleave="handleMouseLeave(rowIndex)"
                  >
                    {{ formatter.number(row[header.fieldName], 'shortDecimal') }}

                    <mds-popover
                      :triggered-by="`fee-breakdown-${rowIndex}`"
                      :visible="row && row.showBreakdown"
                      :position="['bottom-center', 'top-center']"
                    >
                      <mds-section bold :size="7" :title="translate('annualAssetBasedFee')">
                        <mds-list-group size="small">
                          <mds-list-group-item
                            v-for="breakdown in row.breakdown"
                            :key="breakdown.id"
                            :text="`${breakdown.feeName} %`"
                            :microcopy="
                              typeof breakdown.feeAmount !== 'undefined' &&
                                formatter.number(breakdown.feeAmount, 'shortDecimal')
                            "
                          >
                          </mds-list-group-item>
                        </mds-list-group>
                      </mds-section>
                    </mds-popover>
                  </a>
                  <template v-else>
                    {{ formatter.number(row[header.fieldName], 'shortDecimal') }}
                  </template>
                </template>
                <template
                  v-else-if="
                    schedule.feeScheduleType === feeScheduleTypesConst.TOTAL_FEE &&
                      (header.fieldName === 'lowValue' || header.fieldName === 'highValue')
                  "
                >
                  {{ formatter.number(row[header.fieldName], 'shortDecimal') }}
                </template>
                <!-- Values -->
                <template v-else>
                  {{ formatBkptValue(row[header.fieldName]) }}
                </template>
              </mds-td>
            </mds-tr>
          </mds-tbody>
        </mds-table>
      </mds-col>
    </mds-row>
  </mds-layout-grid>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import MdsFieldset from '@mds/fieldset';
import MdsSection from '@mds/section';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import { feeScheduleTypeOptions, FEE_SCHEDULE_TYPES } from '../../_config/constants';
import { autoCalculateHighValue } from '../../utils';
import AssetBasedFeeScheduleMetadataMixin from '../../mixins/metadata/asset-based-fee-schedule-mixin';

export default {
  name: 'fee-schedule-details-view',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsFieldset,
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsPopover,
    MdsSection,
    MdsListGroup,
    MdsListGroupItem,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin, AssetBasedFeeScheduleMetadataMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    schedule: Object,
  },
  data() {
    return {
      rows: [],
    };
  },
  computed: {
    feeScheduleTypeText() {
      const { feeScheduleType } = this.schedule;
      const feeScheduleTypeOption = feeScheduleTypeOptions.find(
        item => item.value === feeScheduleType
      );
      if (feeScheduleTypeOption) {
        const { labelKey } = feeScheduleTypeOption;
        return this.translate(labelKey);
      }
      return null;
    },
    headers() {
      const { feeScheduleType } = this.schedule;
      let headers = [
        {
          text: '',
          fieldName: 'sequence',
          style: {
            width: '60px',
          },
        },
        {
          text: `${
            feeScheduleType === this.feeScheduleTypesConst.TIERED_FEE_BY_INCREMENTAL_VALUE
              ? this.translate('value')
              : this.translate('lowValue')
          } ${this.currencySymbol}`,
          fieldName: 'lowValue',
          align: 'right',
          style: {
            width: '120px',
          },
        },
        {
          text: `${this.translate('highValue')} ${this.currencySymbol}`,
          fieldName: 'highValue',
          align: 'right',
          style: {
            width: '120px',
          },
        },
        {
          text: `${this.translate('annualAssetBasedFee')}`,
          fieldName: 'annualAssetBasedFee',
          align: 'right',
          style: {
            width: '160px',
          },
        },
      ];
      if (feeScheduleType === this.feeScheduleTypesConst.TIERED_FEE_BY_INCREMENTAL_VALUE) {
        headers = headers.filter(h => h.fieldName !== 'highValue');
      }
      return headers;
    },
    hasFeeBreakdown() {
      const { inputFeeType, feeScheduleDetails } = this.schedule;
      return (
        inputFeeType === 1 && feeScheduleDetails[0] && feeScheduleDetails[0].breakdown.length > 0
      );
    },
    formatBkptValue() {
      return bkptValue => {
        if (typeof bkptValue === 'undefined' || bkptValue === null) {
          return '';
        }
        return this.formatter.number(bkptValue, 'integer');
      };
    },
    feeScheduleTypesConst() {
      return FEE_SCHEDULE_TYPES;
    },
  },
  watch: {
    schedule: {
      handler(newVal, oldVal) {
        if (newVal.feeScheduleDetails !== oldVal.feeScheduleDetails) {
          this.rows = this.mapScheduleDetails(newVal.feeScheduleDetails);
        }
      },
      deep: true,
    },
  },
  created() {
    this.rows = this.mapScheduleDetails(this.schedule.feeScheduleDetails);
  },
  methods: {
    mapScheduleDetails(feeScheduleDetails) {
      let rows = feeScheduleDetails;
      const { feeScheduleType } = this.schedule;
      if (
        (feeScheduleType === this.feeScheduleTypesConst.TIERED_FEE_BY_BREAKPOINT ||
          feeScheduleType === this.feeScheduleTypesConst.TOTAL_FEE) &&
        rows &&
        rows.length > 0
      ) {
        let fractionDigits = 0;
        let needMinus = false;
        if (feeScheduleType === this.feeScheduleTypesConst.TOTAL_FEE) {
          // Total Fee
          fractionDigits = 2;
          needMinus = true;
        }
        rows = autoCalculateHighValue(rows, fractionDigits, needMinus);
      }
      return rows.map(item => {
        return {
          ...item,
          showBreakdown: false,
        };
      });
    },
    handleMouseOver(rowIndex) {
      this.rows[rowIndex].showBreakdown = true;
    },
    handleMouseLeave(rowIndex) {
      this.rows[rowIndex].showBreakdown = false;
    },
    getFirstRowSequenceLabel() {
      const first = this.translate('first');
      const tier = `${this.translate('tier')} 1`;
      return { 0: first, 1: tier, 2: '' }[this.schedule.feeScheduleType];
    },
    getMiddleRowSequenceLabel(rowIndex) {
      const next = this.translate('next');
      const tier = `${this.translate('tier')} ${rowIndex + 1}`;
      return { 0: next, 1: tier, 2: '' }[this.schedule.feeScheduleType];
    },
    getLastRowSequenceLabel() {
      const over = this.translate('over');
      const onOrOver = this.translate('onOrOver');
      return { 0: over, 1: over, 2: onOrOver }[this.schedule.feeScheduleType];
    },
  },
};
</script>

<style lang="scss" module>
@import '@mds/typography';
@import '@mds/link';
.field-item {
  margin-bottom: $mds-space-2-x;
  span {
    @include mds-body-text-s();
    display: inline-block;
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mds-fieldset__legend {
    margin-bottom: $mds-space-1-x;
  }
}

.sequence-cell {
  font-size: 12px;
  font-weight: 700;
}
.fee-breakdown {
  @include mds-link($in-table: true);
  cursor: default;
  &.fee-breakdown:hover {
    color: #006fba;
  }
  &.fee-breakdown:focus {
    box-shadow: none;
  }
}

.max-discount-description::before {
  color: #ccc;
  content: '\2022';
  margin-left: $mds-space-quarter-x;
  padding-right: $mds-space-three-quarter-x;
}
</style>
